import { cn } from 'lib/utils'
import { Plus, Sparkles, Trash2 } from 'lucide-react'
import { BrandMemoryService } from 'openapi'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

interface RememberedMemory {
  id: string
  kind: 'fact' | 'timely'
  text: string
  expiresAt: string | null
}

export interface BrandMemoriesListHandle {
  /** Re-pull memories from the server (e.g. after agent tool activity). */
  refresh: () => void
}

interface BrandMemoriesListProps {
  /** Shown when the list is empty; omit to render nothing when empty. */
  emptyText?: string
  /**
   * 'sparkle' (default): acid ✦ markers (inline lists).
   * 'bullet': quiet small dot markers (onboarding panel).
   * 'card': boxed rows with a round acid check marker, matching the Brand Kit
   *   "Post Guidelines" mockup. Text hugs the marker (no flex-1) so RTL content
   *   stays aligned beside it instead of being pushed across the row.
   */
  appearance?: 'sparkle' | 'bullet' | 'card'
  /**
   * Gates the automatic mount fetch (default true) — the onboarding
   * conversation panel mounts before its business exists, where the list
   * endpoint is a guaranteed 403. Fetches as soon as it flips true.
   */
  enabled?: boolean
  /**
   * Fires with the current memory count whenever it changes (load / add /
   * remove / refresh). Lets a parent surface the count elsewhere, e.g. the
   * Brand Kit "Post Guidelines" tab pill.
   */
  onCountChange?: (count: number) => void
  /**
   * Read-only render: drops the "+ add" composer and the per-row remove/edit
   * controls, leaving a calm list. Used by the onboarding recap pane, where
   * captures come from the chat (not manual editing) and acid stays scarce.
   */
  readOnly?: boolean
}

/**
 * The business's Brand Memory — facts and timely notes Taka captured from
 * chats, plus ones the user adds here. Each is removable; an inline composer
 * adds new durable facts. Self-fetching (current business scope); used by the
 * onboarding conversation panel and the Brand Kit page.
 */
export const BrandMemoriesList = forwardRef<BrandMemoriesListHandle, BrandMemoriesListProps>(
  function BrandMemoriesList(
    { emptyText, appearance = 'sparkle', enabled = true, onCountChange, readOnly = false },
    ref
  ) {
    const [memories, setMemories] = useState<RememberedMemory[]>([])
    // Gates count reporting until the first fetch settles. Without this the
    // empty mount render reports 0, then the loaded render reports N — and the
    // parent reads that 0→N jump as an off-tab change and flashes the "unseen"
    // badge on every fresh load. We only report the settled count onward.
    const [loaded, setLoaded] = useState(false)
    // Emphasize newly-captured memories: flash (data-just-approved pulse) and
    // scroll the new bullet into view so the user SEES what the chat just added.
    const [flashIds, setFlashIds] = useState<Set<string>>(new Set())
    const seenIdsRef = useRef<Set<string> | null>(null)
    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scrollTargetRef = useRef<HTMLDivElement | null>(null)
    // Inline edit: which row is open + its working text. Click a card to edit.
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editDraft, setEditDraft] = useState('')

    const refresh = useCallback(async () => {
      try {
        const res = (await BrandMemoryService.brandMemoryControllerListV1()) as {
          memories?: RememberedMemory[]
        }
        setMemories(Array.isArray(res?.memories) ? res.memories : [])
      } catch (err) {
        console.error('brand memories: failed to load', err)
      } finally {
        setLoaded(true)
      }
    }, [])

    useImperativeHandle(ref, () => ({ refresh: () => void refresh() }), [refresh])
    useEffect(() => {
      if (!enabled) return
      void refresh()
    }, [refresh, enabled])

    // Report the count upward whenever it changes. A ref keeps an unstable
    // parent callback from re-firing this on every render.
    const onCountChangeRef = useRef(onCountChange)
    onCountChangeRef.current = onCountChange
    useEffect(() => {
      if (!loaded) return
      onCountChangeRef.current?.(memories.length)
    }, [memories.length, loaded])

    // Detect memories that appeared since the last render and flash + scroll to
    // them. Gated on `loaded`: the empty mount render would otherwise set the
    // baseline to [] and then flash the ENTIRE list as "new" the moment the
    // fetch resolves (visible every time you open the tab). We wait for the
    // first fetch to settle, take that as the baseline (seenIdsRef still null →
    // no flash), and only flash genuinely-new ids from then on.
    useEffect(() => {
      if (!loaded) return
      const ids = memories.map(m => m.id)
      const seen = seenIdsRef.current
      if (seen) {
        const added = ids.filter(id => !seen.has(id))
        if (added.length > 0) {
          setFlashIds(new Set(added))
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
          flashTimerRef.current = setTimeout(() => setFlashIds(new Set()), 1600)
          requestAnimationFrame(() =>
            scrollTargetRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          )
        }
      }
      seenIdsRef.current = new Set(ids)
    }, [memories, loaded])

    useEffect(
      () => () => {
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
      },
      []
    )

    const removeMemory = async (id: string) => {
      setMemories(prev => prev.filter(m => m.id !== id))
      try {
        await BrandMemoryService.brandMemoryControllerRemoveV1(id)
      } catch (err) {
        console.error('brand memories: failed to remove', err)
        void refresh()
      }
    }

    const addMemory = useCallback(async (text: string) => {
      const clean = text.trim()
      if (!clean) return
      try {
        const created = (await BrandMemoryService.brandMemoryControllerCreateV1({
          text: clean,
        })) as RememberedMemory
        // Append the server row (it carries the real id the flash + delete need).
        setMemories(prev => (prev.some(m => m.id === created.id) ? prev : [...prev, created]))
      } catch (err) {
        console.error('brand memories: failed to add', err)
      }
    }, [])

    const beginEdit = (m: RememberedMemory) => {
      setEditingId(m.id)
      setEditDraft(m.text)
    }

    const commitEdit = async (id: string) => {
      const clean = editDraft.trim()
      const current = memories.find(m => m.id === id)
      setEditingId(null)
      // No-op on empty or unchanged text — keep the original.
      if (!clean || !current || clean === current.text) return
      setMemories(prev => prev.map(m => (m.id === id ? { ...m, text: clean } : m)))
      try {
        await BrandMemoryService.brandMemoryControllerUpdateV1(id, { text: clean })
      } catch (err) {
        console.error('brand memories: failed to update', err)
        void refresh()
      }
    }

    return (
      <div>
        {memories.length === 0 && emptyText && (
          <p className="mb-2 text-[13px] leading-relaxed text-paper-dim">{emptyText}</p>
        )}
        {memories.length > 0 && (
          <div className={`flex flex-col ${appearance === 'card' ? 'gap-[9px]' : 'gap-1.5'}`}>
            {memories.map(m => {
              const flash = flashIds.has(m.id)
              const card = appearance === 'card'
              // Card rows are click-to-edit unless the list is read-only (recap pane).
              const interactive = !readOnly && card

              // Card rows are click-to-edit: while editing, swap the row for an
              // inline input. Enter / blur saves, Escape cancels. (Never in
              // read-only mode.)
              if (interactive && editingId === m.id) {
                return (
                  <input
                    key={m.id}
                    autoFocus
                    dir="auto"
                    value={editDraft}
                    onChange={e => setEditDraft(e.target.value)}
                    onBlur={() => void commitEdit(m.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        ;(e.target as HTMLInputElement).blur()
                      }
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                    maxLength={500}
                    className="w-full rounded-[11px] border border-acid bg-bg-3 px-[15px] py-3 text-[14px] text-paper outline-none placeholder:text-paper-faint"
                  />
                )
              }

              return (
                <div
                  key={m.id}
                  ref={flash ? scrollTargetRef : undefined}
                  data-memory-row
                  data-just-approved={flash || undefined}
                  onClick={interactive ? () => beginEdit(m) : undefined}
                  className={
                    card
                      ? cn(
                          'group flex items-start gap-3 rounded-[11px] border border-line bg-bg-3 px-[15px] py-3 transition-colors',
                          interactive && 'cursor-text hover:border-line-bright'
                        )
                      : 'group flex items-start gap-2.5 rounded-[8px] text-[13.5px]'
                  }
                >
                  {/* Marker only on the inline variants: a quiet dot (bullet) or
                      an acid sparkle (sparkle). The card variant has no marker. */}
                  {appearance === 'bullet' ? (
                    <span className="mt-[8px] h-1 w-1 flex-shrink-0 rounded-full bg-[color:var(--paper-faint)]" />
                  ) : appearance === 'sparkle' ? (
                    <Sparkles size={13} className="mt-0.5 flex-shrink-0 text-acid" />
                  ) : null}
                  <span
                    dir="auto"
                    className={
                      card
                        ? 'min-w-0 flex-1 text-[14px] leading-[1.45] font-medium break-words text-paper'
                        : 'min-w-0 flex-1 leading-snug break-words text-paper-dim'
                    }
                  >
                    {m.text}
                    {m.kind === 'timely' && (
                      <span className="mono ml-1.5 text-[9px] text-paper-faint uppercase">
                        timely
                      </span>
                    )}
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      aria-label={card ? 'Remove guideline' : 'Remove from profile'}
                      onClick={e => {
                        e.stopPropagation()
                        void removeMemory(m.id)
                      }}
                      className={
                        card
                          ? // Card: always-visible, clearly-tappable remove control.
                            'mt-0.5 -mr-1 ml-auto flex-none rounded-[6px] p-1 text-paper-dim transition-colors hover:bg-bg-2 hover:text-destructive'
                          : // Inline lists stay calm: reveal the control on row hover.
                            'mt-0.5 flex-shrink-0 text-paper-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive'
                      }
                    >
                      <Trash2 size={card ? 15 : 13} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {!readOnly && <AddMemoryRow appearance={appearance} onAdd={addMemory} />}
      </div>
    )
  }
)

/**
 * Quiet "+ Add a memory" affordance that expands into an inline composer.
 * Enter (or the check) saves; Escape or an empty blur collapses it. Keeps the
 * list calm and read-only until the user opts to add.
 */
function AddMemoryRow({
  appearance,
  onAdd,
}: {
  appearance: 'sparkle' | 'bullet' | 'card'
  onAdd: (text: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const commit = async () => {
    const text = draft.trim()
    setEditing(false)
    setDraft('')
    if (text) await onAdd(text)
  }

  if (editing) {
    // The card variant boxes the composer to match the guideline rows; the
    // inline variants use a quiet underline so the list stays calm.
    return (
      <input
        autoFocus
        dir="auto"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ;(e.target as HTMLInputElement).blur()
          }
          if (e.key === 'Escape') {
            setDraft('')
            setEditing(false)
          }
        }}
        placeholder={appearance === 'card' ? 'Add a guideline…' : 'Something to note…'}
        maxLength={500}
        className={
          appearance === 'card'
            ? 'mt-[9px] w-full rounded-[11px] border border-acid bg-bg-3 px-[15px] py-3 text-[14px] text-paper outline-none placeholder:text-paper-faint'
            : 'mt-2 w-full border-b border-acid bg-transparent pb-1 text-[13.5px] text-paper outline-none placeholder:text-paper-faint'
        }
      />
    )
  }

  if (appearance === 'card') {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-[9px] flex w-full items-center gap-2 rounded-[11px] border border-dashed border-line-bright px-[15px] py-3 text-[13.5px] text-paper-dim transition-colors hover:border-acid hover:text-paper"
      >
        <Plus size={16} className="flex-none text-acid" />
        Add a guideline manually
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`inline-flex items-center gap-1.5 text-[13px] font-medium text-acid transition-opacity hover:opacity-80 ${
        appearance === 'sparkle' ? 'mt-2.5' : 'mt-2'
      }`}
    >
      <Plus size={14} className="flex-shrink-0" />
      Add a note
    </button>
  )
}
